import classNames from 'classnames';
import CustomLoader from 'components/molecules/CustomLoader.component';
import SimpleError from 'components/molecules/SimpleError.component';
import {
  useGroupSubscriptionPlans,
  useSubscriptionPlan,
} from 'hooks/Subscriptions';
import { useEffect, useMemo, useState, type FC } from 'react';
import type {
  PaywallFeatureType,
  SubscriptionPlan,
} from 'types/subscriptions.types';
import type { SubscriptionPlanNameEnum } from 'types/users.types';
import { legacyPlans } from 'utils/constants';
import { excludeNumbers } from 'utils/helpers';
import formatMoney from 'utils/formatMoney';
import ModalHeading from 'components/molecules/ModalHeading';
import {
  useGetWorldThemeFromPath,
  worldThemeContext,
} from 'hooks/shared/useWorldTheme';
import { useTranslation } from 'react-i18next';
import useStripeSubscription from 'hooks/Subscriptions/useStripeSubscription';
import { Button } from 'components/atoms/Button';
import { logAmplitude } from 'utils/amplitude';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import Diamond from 'components/atoms/SVG/Diamond.component';
import type { IColors } from 'theme/colors';
import FirstOfMonthCalendar from 'components/atoms/SVG/FirstOfMonthCalendar.component';
import PaywallUnlockerIcon from 'components/atoms/SVG/PaywallUnlockerIcon.component';
import { WEBAPP_PAYWALLMODAL_OPENED } from 'utils/amplitude/events/global.amplitude';

type Props = {
  lockKey?: string;
  title: string | ((planName: string) => string);
  description:
    | string
    | ((
        planName: string,
        currentCount: number,
        cheapestUnlockerPlanCount: number,
      ) => string);
  eventName?: string;
  counterKey?: string;
  currentCount?: number;
  primaryFeature?:
    | PaywallFeatureType
    | ((features: SubscriptionPlan['features']) => PaywallFeatureType);
};

const themes = {
  taxes: {
    iconClassName:
      'bg-taxes-100 text-taxes-900 stroke-taxes-900 fill-taxes-900',
    badgeClassName: 'bg-taxes-100 text-taxes-700',
    priceClassName: 'border-taxes-500',
  },
  bank: {
    iconClassName: 'bg-bank-100 text-bank-900 stroke-bank-900 fill-bank-900',
    badgeClassName: 'bg-bank-100 text-bank-700',
    priceClassName: 'border-bank-500',
  },
  expenses: {
    iconClassName:
      'bg-expenses-100 text-expenses-900 stroke-expenses-900 fill-expenses-900',
    badgeClassName: 'bg-expenses-100 text-expenses-700',
    priceClassName: 'border-expenses-500',
  },
  primary: {
    iconClassName:
      'bg-primary-100 text-primary-900 stroke-primary-900 fill-primary-900',
    badgeClassName: 'bg-primary-100 text-primary-700',
    priceClassName: 'border-primary-500',
  },
  revenues: {
    iconClassName:
      'bg-revenues-100 text-revenues-900 stroke-revenues-900 fill-revenues-900',
    badgeClassName: 'bg-revenues-100 text-revenues-700',
    priceClassName: 'border-revenues-500',
  },
};

const FeatureItem: FC<{
  feature: PaywallFeatureType;
  iconClassName: string;
}> = ({ feature, iconClassName }) => (
  <div className="flex items-start gap-2.5">
    <div
      className={classNames(
        iconClassName,
        'flex items-center justify-center flex-shrink-0 w-8 h-8 p-1.5 rounded-full',
      )}
    >
      <feature.Icon className="w-6 h-6" />
    </div>

    <div className="flex-1">
      <h3 className="font-semibold text-black">{feature.title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">
        {feature.description}
      </p>
    </div>
  </div>
);

const PaywallLockV2: FC<Props> = ({
  lockKey,
  title,
  description,
  counterKey,
  currentCount,
  eventName,
  primaryFeature,
}) => {
  const { t } = useTranslation();

  const navigate = useCustomNavigate();

  const [selectedPlan, setSelectedPlan] =
    useState<SubscriptionPlanNameEnum | null>(null);

  const subscriptionPlan = useSubscriptionPlan();

  const {
    data: subscriptionPlans = [],
    isLoading,
    isError,
    refetch,
  } = useGroupSubscriptionPlans(subscriptionPlan?.planInternalName);

  const { subscribe, isLoading: isLoadingSubscribe } = useStripeSubscription();

  const cheapestUnlockerPlans = useMemo(() => {
    const activePlans = subscriptionPlans?.filter(
      (plan) => !legacyPlans.includes(plan.nickname),
    );

    const cheapestActivePlan = activePlans
      ?.filter((plan: SubscriptionPlan) => {
        const planFeatures = plan.features.locks;

        const planCount =
          plan.features.counters?.[
            counterKey as keyof typeof plan.features.counters
          ]?.total;

        if (counterKey && currentCount) {
          return (
            (typeof planCount === 'number' &&
              typeof currentCount === 'number' &&
              planCount > currentCount) ||
            planCount === 'INFINITE'
          );
        }

        return !planFeatures[lockKey as keyof typeof planFeatures];
      })
      .reduce(
        (cheapest: SubscriptionPlan | null, current: SubscriptionPlan) => {
          if (!cheapest) return current;

          return current.amount < cheapest.amount ? current : cheapest;
        },
        null as SubscriptionPlan | null,
      );

    return activePlans
      ?.filter((plan) => {
        const baseNickname = excludeNumbers(plan.shortNickname);

        const targetBaseNickname = excludeNumbers(
          cheapestActivePlan?.shortNickname,
        );

        return baseNickname === targetBaseNickname;
      })
      ?.sort((a, b) => a.amount - b.amount);
  }, [lockKey, subscriptionPlans, counterKey, currentCount]);

  useEffect(() => {
    if (cheapestUnlockerPlans.length > 0 && !selectedPlan) {
      setSelectedPlan(
        cheapestUnlockerPlans[1].nickname as SubscriptionPlanNameEnum,
      );
    }
  }, [cheapestUnlockerPlans, selectedPlan]);

  useEffect(() => {
    if (eventName) {
      logAmplitude(eventName);
    } else {
      logAmplitude(WEBAPP_PAYWALLMODAL_OPENED, {
        feature: counterKey || lockKey || '',
      });
    }
  }, [eventName]);

  const worldColor = useGetWorldThemeFromPath();

  if (isLoading) {
    return <CustomLoader className="min-h-[300px]" />;
  }

  if (isError || !cheapestUnlockerPlans || !cheapestUnlockerPlans.length) {
    return (
      <SimpleError
        messageClassName="!text-xl"
        className="min-h-[300px]"
        onRetry={() => refetch()}
      />
    );
  }

  const selectedPlanObject =
    cheapestUnlockerPlans.find((plan) => plan.nickname === selectedPlan) ||
    cheapestUnlockerPlans[0];

  const recommendedPlanFeatures = selectedPlanObject?.featuresContent;

  const recommendedPlanName = t(selectedPlanObject?.nickname);

  const _primaryFeature =
    typeof primaryFeature === 'function'
      ? primaryFeature(selectedPlanObject?.features)
      : primaryFeature;

  const { iconClassName, badgeClassName, priceClassName } =
    themes[worldColor as keyof typeof themes] || {};

  return (
    <worldThemeContext.Provider value={worldColor}>
      <div className="flex flex-col justify-center items-center">
        <ModalHeading
          title={
            typeof title === 'function' ? title(recommendedPlanName) : title
          }
          titleClassName="font-degular text-black"
          subtitle={
            typeof description === 'function'
              ? description(
                  recommendedPlanName,
                  currentCount || 0,
                  (selectedPlanObject.features.counters as any)?.[
                    counterKey as any
                  ]?.total,
                )
              : description
          }
          Icon={() => (
            <PaywallUnlockerIcon
              color={worldColor as IColors}
              className="w-12 h-12"
            />
          )}
          iconClassName="w-12 h-12"
          iconWrapper={false}
        />

        <div className="flex-1 w-full my-6 relative">
          <div className="space-y-4">
            {_primaryFeature ? (
              <FeatureItem
                feature={_primaryFeature}
                iconClassName={iconClassName}
              />
            ) : null}

            {recommendedPlanFeatures
              ?.filter((item) => item.title !== _primaryFeature?.title)
              ?.slice(0, _primaryFeature ? 2 : 3)
              ?.map((feature, index: number) => (
                <FeatureItem
                  key={index}
                  feature={feature}
                  iconClassName={iconClassName}
                />
              ))}
          </div>
        </div>

        <div className="p-10 pt-2 flex flex-col w-full items-center relative">
          <h3 className="font-degular text-center text-xl font-bold text-primary-800 mb-5">
            {t('feature_lock_paywall.upgrade_text')}
          </h3>

          <div className="flex w-[90%] gap-5 items-center relative">
            {cheapestUnlockerPlans.map((plan, index) => {
              const savings = (() => {
                if (plan.interval === 'month') {
                  return null;
                }

                const yearlyTotal = plan.amount / 100;

                const monthlyAmount =
                  cheapestUnlockerPlans.find((p) => p.interval === 'month')
                    ?.amount || 0;

                const monthlyTotal = (monthlyAmount / 100) * 12;

                const savings = monthlyTotal - yearlyTotal;

                return savings > 0 ? savings : null;
              })();

              return (
                <button
                  key={index}
                  type="button"
                  className={classNames(
                    'relative flex-1 p-4 rounded-xl border transition-all duration-200 cursor-pointer bg-white',
                    {
                      [priceClassName]: selectedPlan === plan.nickname,
                    },
                  )}
                  onClick={() => setSelectedPlan(plan.nickname)}
                >
                  {savings ? (
                    <div className="absolute -top-4 left-0 right-0">
                      <span
                        className={classNames(
                          badgeClassName,
                          'px-2 py-1 rounded-full text-xs font-medium',
                        )}
                      >
                        {t('feature_lock_paywall.save_text', {
                          amount: formatMoney(savings),
                        })}
                      </span>
                    </div>
                  ) : null}

                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      {plan.interval === 'month' ? (
                        <FirstOfMonthCalendar className="w-7 h-7" />
                      ) : (
                        <Diamond
                          color={worldColor as IColors}
                          className="w-7 h-7"
                        />
                      )}
                    </div>

                    <div className="text-sm font-medium text-black mb-1">
                      {plan.interval === 'month'
                        ? t('feature_lock_paywall.one_month')
                        : t('feature_lock_paywall.one_year')}
                    </div>

                    <div className="font-degular text-2xl font-bold text-black mb-2">
                      {formatMoney(plan.amount / 100)}
                    </div>

                    <div className="text-xs text-gray-800">
                      {t('paywall.tabs.you_pay_less', {
                        price: `€${(
                          plan.amount /
                          100 /
                          (plan.interval === 'month' ? 1 : 12)
                        ).toFixed(2)}`,
                      })}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <Button
          className="w-full"
          disabled={!selectedPlan}
          onClick={() => subscribe(selectedPlanObject?.nickname)}
          loading={isLoadingSubscribe}
        >
          {t('feature_lock_paywall.upgrade_cta', {
            planName: t(selectedPlanObject?.nickname),
          })}
        </Button>

        <Button
          className="w-full mt-4"
          structure="text"
          onClick={() => {
            navigate('/subscriptions');
          }}
          loading={isLoadingSubscribe}
        >
          {t('feature_lock_paywall.see_all_plans_cta')}
        </Button>
      </div>
    </worldThemeContext.Provider>
  );
};

export default PaywallLockV2;
